import React from 'react';
import { Input } from '@usepepper/sprout-kit-ui';
import { useCartQuantityInput } from '@/experiences/sales-portal/domain/ordering/screens/order-entry/blocs/hooks/use-cart-quantity-input';

export interface VariantPackQuantityInputProps {
  isQuantityDisabled?: boolean;
  onSaveCart: () => Promise<unknown>;
  /** Forwarded to {@link useCartQuantityInput} — the card curries
   *  `updateCartItemQuantity` so this leaf doesn't reach into the cart
   *  store for writes. */
  onUpdateQuantity: (quantity: number) => boolean;
  variantPackUUID: string;
}

const INPUT_WIDTH = 48;

/**
 * Quantity input rendered inside a {@link VariantPackCard}. Wires the
 * presentational {@link QuantityInput} to the cart store via
 * {@link useCartQuantityInput} so changes persist on blur.
 *
 * @param props - {@link VariantPackQuantityInputProps}.
 * @returns The rendered input.
 */
export const VariantPackQuantityInput: React.FC<
  VariantPackQuantityInputProps
> = ({ isQuantityDisabled, onSaveCart, onUpdateQuantity, variantPackUUID }) => {
  // TODO(SRG-238): hoist cart-store reads + change/blur handling into the
  // panel container so this leaf becomes purely presentational. Calling
  // useCartQuantityInput here couples the leaf to the data layer.
  const { onChangeQuantity, onSaveQuantity, quantity } = useCartQuantityInput({
    disabled: isQuantityDisabled,
    onUpdateQuantity,
    saveCart: onSaveCart,
    variantPackUUID,
  });

  return (
    <Input
      disabled={Boolean(isQuantityDisabled)}
      keyboardType="decimal-pad"
      onBlur={onSaveQuantity}
      onChangeText={onChangeQuantity}
      paddingHorizontal={0}
      placeholder="0"
      selectTextOnFocus
      size="$md"
      textAlign="center"
      value={quantity}
      width={INPUT_WIDTH}
    />
  );
};
